import { useEffect, useState } from "react";
import { ArrowLeft, MessageSquare, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConversationManager({ token, setError }) {
  const [convos, setConvos] = useState(null);
  const [open, setOpen] = useState(null);
  const [messages, setMessages] = useState([]);
  const [member, setMember] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = () =>
    fetch(`${API}/api/admin/conversations`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setConvos(d.conversations))
      .catch(() => setError("Chargement impossible."));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openConvo = async (cv) => {
    setOpen(cv);
    setDraft("");
    try {
      const res = await fetch(`${API}/api/admin/conversations/${cv.id}`, { headers });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setMessages(data.messages);
      setMember(data.member);
      setConvos((cs) => cs.map((c) => (c.id === cv.id ? { ...c, unread: 0 } : c)));
    } catch {
      setError("Chargement impossible.");
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !open) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/conversations/${open.id}/messages`, {
        method: "POST",
        headers,
        body: JSON.stringify({ body: draft }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setMessages((ms) => [...ms, data.message]);
      setDraft("");
      load();
    } catch {
      setError("Envoi impossible.");
    } finally {
      setBusy(false);
    }
  };

  if (open) {
    return (
      <div data-testid="admin-conversation-thread" className="mt-2">
        <button
          data-testid="admin-convo-back"
          onClick={() => setOpen(null)}
          className="group flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ivory/45 transition-colors duration-300 hover:text-ivory"
        >
          <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" /> Messages
        </button>
        <div className="mt-6 border border-ivory/12 p-6 md:p-8">
          <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-ivory/10 pb-5">
            <div>
              <h2 className="font-serif text-2xl font-medium text-ivory">{open.property_title}</h2>
              <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-bronze">{open.property_ref}</p>
            </div>
            {member && (
              <p className="text-xs text-ivory/55">
                {member.first_name} {member.last_name} · {member.company || "—"} ·{" "}
                <a href={`mailto:${member.email}`} className="text-champagne">{member.email}</a>
              </p>
            )}
          </div>
          <div className="mt-6 max-h-96 space-y-4 overflow-y-auto pr-1" data-testid="admin-chat-messages">
            {messages.length === 0 && <p className="text-sm italic text-ivory/40">Aucun message.</p>}
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-4 py-3 text-sm font-light leading-relaxed ${
                    msg.sender === "admin" ? "bg-champagne/15 text-ivory" : "border border-ivory/15 text-ivory/80"
                  }`}
                >
                  <p className="mb-1 flex items-center gap-3 text-[9px] uppercase tracking-[0.2em] text-ivory/40">
                    {msg.sender === "admin" ? "FLEN" : open.member_name || "Membre"}
                    <span>{new Date(msg.created_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}</span>
                  </p>
                  {msg.body}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="mt-6 flex items-end gap-3 border-t border-ivory/10 pt-5">
            <textarea
              rows={2}
              data-testid="admin-chat-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Votre réponse… (le membre reçoit un e-mail)"
              className="w-full flex-1 resize-none border-0 border-b border-ivory/25 bg-transparent px-0 py-2 text-sm text-ivory outline-none transition-colors duration-300 placeholder:text-ivory/30 focus:border-champagne"
            />
            <button
              type="submit"
              data-testid="admin-chat-send"
              disabled={busy || !draft.trim()}
              className="flex items-center gap-2 bg-champagne px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-500 hover:bg-ivory disabled:opacity-50"
            >
              <Send size={13} /> Envoyer
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="conversation-manager" className="mt-2">
      <p className="text-[11px] uppercase tracking-[0.25em] text-ivory/45">
        {convos ? `${convos.length} conversation${convos.length > 1 ? "s" : ""}` : "…"}
      </p>
      {!convos ? (
        <p className="mt-10 text-ivory/50">Chargement…</p>
      ) : convos.length === 0 ? (
        <p data-testid="conversations-empty" className="mt-10 text-ivory/50">
          Aucune conversation. Les messages des membres sur les objets off-market apparaissent ici.
        </p>
      ) : (
        <div className="mt-8 divide-y divide-ivory/10 border-y border-ivory/10" data-testid="conversations-list">
          {convos.map((cv) => (
            <button
              key={cv.id}
              data-testid={`admin-convo-${cv.id}`}
              onClick={() => openConvo(cv)}
              className="group grid w-full items-baseline gap-2 px-2 py-6 text-left transition-colors duration-300 hover:bg-ivory/[0.02] md:grid-cols-[220px_1fr_auto] md:gap-8"
            >
              <div>
                <p className="text-sm text-ivory">{cv.member_name}</p>
                <p className="text-xs text-ivory/45">{cv.member_company || cv.member_email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-ivory transition-colors duration-300 group-hover:text-champagne">
                  {cv.property_title} <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-bronze">{cv.property_ref}</span>
                </p>
                {cv.last_message && (
                  <p className="mt-1 line-clamp-1 text-xs font-light text-ivory/45">
                    {cv.last_message.sender === "admin" ? "FLEN" : cv.member_name} · {cv.last_message.body}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4 md:justify-end">
                {cv.unread > 0 && (
                  <span data-testid={`admin-convo-unread-${cv.id}`} className="border border-champagne/60 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-champagne">
                    {cv.unread} non lu{cv.unread > 1 ? "s" : ""}
                  </span>
                )}
                <span className="text-[11px] tabular-nums text-ivory/35">
                  {cv.last_message_at ? new Date(cv.last_message_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" }) : "—"}
                </span>
                <MessageSquare size={14} className="text-champagne transition-transform duration-300 group-hover:scale-110" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
